import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import SummarySectionHeader from "../../components/applicantDetailsTabs/SummarySectionHeader";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";

interface ApplicantDetailsTabProps {
  applicantadditionalDetails: any[];
  isMobile: boolean;
  maritalStatusMap?: Record<string, string>;
  isDIYJourney?: boolean;
}

const ApplicantAdditionalTab: React.FC<ApplicantDetailsTabProps> = ({
    applicantadditionalDetails,
  isMobile,
  maritalStatusMap,
  isDIYJourney = false,
}) => {
  // Loan Tenure (partner-started journey) comes back from Processio as a
  // LOAN_TENURE_INMONTH system-value code — fetch that master here and
  // resolve the code to its display label before rendering.
  const [loanTenureVal, setLoanTenureVal] = useState<
    Array<{ value: string; label: string; id: string }>
  >([]);

  useEffect(() => {
    const fetchLoanTenureMaster = async () => {
      try {
        const response = await eligibilityCheckServices.getSystemValues();
        if (response?.data?.status === "success") {
          const systemValues = response.data.data;
          const tenureOptions = systemValues
            .filter(
              (item: any) => item.name === "LOAN_TENURE_INMONTH" && item.active
            )
            .map((item: any) => ({
              value: item.code,
              label: item.value,
              id: item.serialId,
            }));
          setLoanTenureVal(tenureOptions);
        }
      } catch (error) {
        console.error("Failed to fetch loan tenure master", error);
      }
    };
    fetchLoanTenureMaster();
  }, []);

  if (!applicantadditionalDetails || applicantadditionalDetails.length === 0)
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  // define specific order of keys
  const applicantDependents = [
    "applicantDependents",
    "applicantNationality",
    "applicantMaritalStatus",
    "applicantBirthCountry",
    "applicantCityCountry",
  ];

  const loanKeys = [
    "loanLoanAmount",
    // Requested Tenure and Requested ROI are hardcoded by the backend
    // (120 / 13) for the DIY journey, so hide them from the overview.
    ...(isDIYJourney ? [] : ["loanLoanTenure", "loanRequestedRoi"]),
    "loanDateOfDisbursement"
  ];

  // displaying this fields separately
  const totalExpenseObj = applicantadditionalDetails.find((item) => item.key === "totalExpenseAmt");
  const totalFundsObj = applicantadditionalDetails.find((item) => item.key === "totalFundsAmt");

  // find and sort items based on the key list
  const customLabels: Record<string, string> = {
    "Applicant Dependents": "No of Dependents",
    "Applicant Birth Country":"Country Of Birth",
    "Applicant City Country":"City Of Birth",
    "Loan Date Of Disbursement":"Expected Of Disbursement",
    "Loan Requested Roi":"Requested ROI",
    "Loan Loan Tenure":"Loan Tenure",
    "Loan Loan Amount":"Loan Amount",
  };
  const getOrderedData = (keys: string[]) => {
    return keys
      .map((key) => applicantadditionalDetails.find((item) => item.key === key))
      .filter((item): item is NonNullable<typeof item> => item != null)
      .map((item) => {
        // Check if custom label exists, otherwise strip leading "Applicant "
        const updatedLabel =
          customLabels[item.label] ||
          item.label.replace(/^Applicant\s+/i, "").trim();

        let updatedValue = item.value;

        // 1. Marital Status Check
        if (
          (item.key === "applicantMaritalStatus" || item.name === "applicantMaritalStatus") &&
          item.value != null
        ) {
          updatedValue =
            maritalStatusMap?.[String(item.value).trim().toUpperCase()] ?? item.value;
        }
        // 2. Loan Tenure Check
        else if (
          (item.key === "loanLoanTenure" || item.name === "loanLoanTenure") &&
          item.value != null &&
          item.value !== ""
        ) {
          updatedValue =
            loanTenureVal.find(
              (opt) =>
                String(opt.id) === String(item.value) ||
                String(opt.label) === String(item.value)
            )?.label ?? item.value;
        }
        // 3. Disbursement Date Check (DD-MM-YYYY Conversion)
        else if (
          (item.key === "loanDateOfDisbursement" || item.name === "loanDateOfDisbursement") &&
          item.value
        ) {
          const dateStr = String(item.value).trim();
          
          // Handles "YYYY-MM-DD" or ISO strings like "2026-08-28T00:00:00"
          if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const [year, month, day] = dateStr.split("T")[0].split("-");
            updatedValue = `${day}-${month}-${year}`;
          } else {
            // General fallback using Date object
            const parsedDate = new Date(dateStr);
            if (!isNaN(parsedDate.getTime())) {
              const day = String(parsedDate.getDate()).padStart(2, "0");
              const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
              const year = parsedDate.getFullYear();
              updatedValue = `${day}-${month}-${year}`;
            }
          }
        }

        return {
          ...item,
          label: updatedLabel,
          value: updatedValue,
        };
      });
  };
  
  const expenseData = getOrderedData(applicantDependents);
  const fundsData = getOrderedData(loanKeys);
  const renderGridItems = (items: any[]) => {
    return items.map((field: any, subIndex: number) => (
      <Grid item xs={12} sm={6} md={3} key={`${field.key}-${subIndex}`} sx={{ ml: isMobile ? 2 : 0 }}>
        <DetailRow
          item={{ label: field.label, value: field.value, isVerified: false }}
        />
      </Grid>
    ));
  };

  // Reusable style for the headers
  const headerStyle = {
    fontSize: "15px",
    fontWeight: 600,
    color: "#6f6b7d",
    mb: 1,
  };

  const highlightStyle = {
    fontSize: "13px",
    fontWeight: 700,
    color: "#04a0a2",
    ml: 1,
    mb: 1,
  };

  return (
    <Grid container xs={12} spacing={2} sx={{ pl: isMobile ? 0 : 3 }}>
      {/* expense section */}
      <Grid item xs={12} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <SummarySectionHeader title="Applicant Details" />
        {totalExpenseObj && (
          <Typography sx={highlightStyle}>
            | {totalExpenseObj.label}: ₹ {new Intl.NumberFormat("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(Number(totalExpenseObj.value))}
          </Typography>
        )}
      </Grid>

      {renderGridItems(expenseData)}

      {/* funds section */}
      <Grid item xs={12} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <SummarySectionHeader title=" Loan Details" />
        
      </Grid>

      {renderGridItems(fundsData)}
    </Grid>
  );
};

export default ApplicantAdditionalTab;
